<template>
	<Teleport to="body">
		<div v-if="lessonPlan" class="lesson-plan-print">
			<table class="print-table">
				<thead>
					<tr><td><div class="print-running-header">{{ lessonPlan.title }}</div></td></tr>
				</thead>

				<tbody>
					<tr>
						<td class="print-content">
							<h1 class="text-xl font-semibold">{{ lessonPlan.title }}</h1>
							<div v-if="lessonPlan.creatorName" class="flex items-center gap-2 text-xs text-accent-focus bg-accent-focus/10 py-0.5 px-2 mt-1 mb-6 max-w-max rounded">
								<p>{{ lessonPlan.creatorName }}</p>
								<div v-if="lessonPlan.coCreatorName" class="flex items-center gap-2">
									<span>/</span><span>{{ lessonPlan.coCreatorName }}</span>
								</div>
							</div>
							<div v-else class="mb-6"></div>

							<div class="grid grid-cols-2 gap-4 mb-8 font-medium">
								<section class="print-block border-2 border-base-100 rounded-lg p-4">
									<h2 class="font-semibold mb-2 text-xs uppercase text-base-content-light">{{ $t("Supplies") }}</h2>
									<ul v-if="lessonPlan.supplies?.length" class="list-disc pl-5">
										<li v-for="(item, idx) in lessonPlan.supplies" :key="idx">{{ item }}</li>
									</ul>
									<p v-else class="text-sm italic text-base-content-light">{{ $t("None") }}</p>
								</section>

								<section class="print-block border-2 border-base-100 rounded-lg p-4">
									<h2 class="font-semibold mb-2 text-xs uppercase text-base-content-light">{{ $t("Attachments") }}</h2>
									<ul v-if="lessonPlan.attachments?.length" class="list-disc pl-5">
										<li v-for="(file, idx) in lessonPlan.attachments" :key="file.id ?? idx">{{ file.name }}</li>
									</ul>
									<p v-else class="text-sm italic text-base-content-light">{{ $t("None") }}</p>
								</section>
							</div>

							<TextEditor
								v-if="lessonPlan.instructions"
								class="mb-8"
								:modelValue="lessonPlan.instructions"
								:readOnly="true"
							/>

							<section class="px-4">
								<h2 class="font-semibold mb-2 text-xs uppercase text-base-content-light">{{ $tc("Goal", 2) }}</h2>
								<template v-if="goalGroups.length">
									<div v-for="group in goalGroups" :key="group.key" class="mb-3">
										<h3 v-if="goalGroups.length > 1" class="font-semibold mb-1">
											{{ group.curriculum.name || $t('Other') }}
										</h3>
										<GoalsDisplayGroup :group="group" />
									</div>
								</template>
								<p v-else class="text-sm italic text-base-content-light">{{ $t("None") }}</p>
							</section>
						</td>
					</tr>
				</tbody>

				<tfoot>
					<tr><td><div class="print-spacer"></div></td></tr>
				</tfoot>
			</table>
		</div>
	</Teleport>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import TextEditor from "@/components/ui/TextEditor.vue";
import GoalsDisplayGroup from "@/components/GoalsDisplayGroup.vue";
import { Curriculum, CurriculumGoalGroup } from "@/models/Curriculum.model";
import { Goal } from "@/models/Goal.model";
import { LessonPlan } from "@/models/LessonPlan.model";
import { useOrganisationsStore } from "@/stores/Organisations.store";

export default {
	components: { TextEditor, GoalsDisplayGroup },

	props: {
		lessonPlan: {
			type: Object as () => LessonPlan,
			default: null,
		},
	},

	computed: {
		...mapStores(useOrganisationsStore),

		// Always expanded groups (no accordion), since collapsed content wouldn't print.
		goalGroups(): CurriculumGoalGroup[] {
			if (!this.lessonPlan?.goals?.length) return [];
			return Curriculum.groupGoals(
				this.lessonPlan.goals as Goal[],
				(this.organisationsStore.currentOrganisation?.curriculum ?? null) as Curriculum | null,
			);
		},
	},
};
</script>

<style>
.lesson-plan-print {
	display: none;
}

/* A zero page margin also suppresses the browser's header/footer (date, page URL).
   Kept at top level (not inside @media print) and !important so no other @page rule wins. */
@page {
	size: auto;
	margin: 0 !important;
}

@media print {
	body > *:not(.lesson-plan-print) {
		display: none !important;
	}

	.lesson-plan-print {
		display: block;
		color: #000;
		background: #fff;
		print-color-adjust: exact;
		-webkit-print-color-adjust: exact;
	}

	/* Table layout trick: repeating header/footer spacers = margin on every page */
	.lesson-plan-print .print-table {
		width: 100%;
		border-collapse: collapse;
		border: 0;
	}

	.lesson-plan-print .print-table > thead {
		display: table-header-group;
	}

	.lesson-plan-print .print-table > tfoot {
		display: table-footer-group;
	}

	.lesson-plan-print .print-table td {
		padding: 0;
		border: 0;
	}

	/* Repeats at the top of every page (replaces the browser header) */
	.lesson-plan-print .print-running-header {
		box-sizing: border-box;
		height: 14mm;
		padding-top: 4mm;
		padding-bottom: 6mm;
		font-size: 9pt;
		color: #666;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* Bottom margin on every page */
	.lesson-plan-print .print-spacer {
		height: 12mm;
	}

	/* Side margins (top/bottom come from the spacers) */
	.lesson-plan-print .print-table td.print-content {
		padding: 0 15mm;
		/* Allow the long content row to split across pages */
		break-inside: auto;
	}

	.lesson-plan-print .print-block {
		break-inside: avoid;
	}

	.lesson-plan-print h1,
	.lesson-plan-print h2,
	.lesson-plan-print h3 {
		break-after: avoid;
	}
}
</style>
